import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Divider, Grid, Tab, Tabs, Typography } from '@material-ui/core';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
    fetchWalletThunk,
    fetchTransactionsThunk,
    fetchRechargesThunk,
    fetchTemplatesThunk,
    fetchPackagesThunk,
    initiateRechargeThunk,
    resetWhatsAppState,
    selectWhatsAppWallet,
    selectWhatsAppWalletApi,
    selectAllTransactions,
    selectWhatsAppTransactionsApi,
    selectAllRecharges,
    selectWhatsAppRechargesApi,
    selectAllTemplates,
    selectWhatsAppTemplatesApi,
    selectAllPackages,
    selectWhatsAppPackagesApi,
    selectWhatsAppRechargeInitiateApi,
} from 'store/reducers/temple/whatsappSlice';
import { selectTempleByUrl, staffTempleRetrieveThunk } from 'store/reducers/temple/templeSlice';
import { useSnackbar } from 'notistack';
import TempleSearch from 'containers/Staff/TempleSearch';
import WalletCard from './WalletCard';
import TransactionTable from './TransactionTable';
import RechargeTable from './RechargeTable';
import TemplateTable from './TemplateTable';
import RechargeDialog from './RechargeDialog';
import AdminPackageTable from './AdminPackageTable';
import AdminTemplateTable from './AdminTemplateTable';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box pt={3}>{children}</Box>}
    </div>
);

const WhatsApp: React.FC = () => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [tab, setTab] = useState(0);
    const [rechargeDialogOpen, setRechargeDialogOpen] = useState(false);
    const [selectedTempleUrl, setSelectedTempleUrl] = useState('');

    const selectedTemple = useAppSelector(state => selectTempleByUrl(state, selectedTempleUrl));
    const templeId = selectedTemple?.id;

    const wallet = useAppSelector(selectWhatsAppWallet);
    const walletApi = useAppSelector(selectWhatsAppWalletApi);
    const transactions = useAppSelector(selectAllTransactions);
    const transactionsApi = useAppSelector(selectWhatsAppTransactionsApi);
    const recharges = useAppSelector(selectAllRecharges);
    const rechargesApi = useAppSelector(selectWhatsAppRechargesApi);
    const templates = useAppSelector(selectAllTemplates);
    const templatesApi = useAppSelector(selectWhatsAppTemplatesApi);
    const packages = useAppSelector(selectAllPackages);
    const packagesApi = useAppSelector(selectWhatsAppPackagesApi);
    const rechargeInitiateApi = useAppSelector(selectWhatsAppRechargeInitiateApi);

    const handleTempleOpen = useCallback((url: string) => {
        dispatch(resetWhatsAppState());
        setSelectedTempleUrl(url);
        setTab(0);
        dispatch(staffTempleRetrieveThunk({ url }));
    }, [dispatch]);

    // Fetch wallet and transactions when temple is selected
    useEffect(() => {
        if (templeId) {
            dispatch(fetchWalletThunk({ templeId }));
            dispatch(fetchTransactionsThunk({ templeId }));
        }
    }, [dispatch, templeId]);

    // Fetch tab-specific data lazily (temple-scoped tabs are index 0-2)
    useEffect(() => {
        if (!templeId) return;
        if (tab === 1) {
            dispatch(fetchRechargesThunk({ templeId }));
        } else if (tab === 2) {
            dispatch(fetchTemplatesThunk({ templeId }));
        }
    }, [dispatch, tab, templeId]);

    const handleRechargeOpen = () => {
        if (templeId) {
            dispatch(fetchPackagesThunk({ templeId }));
        }
        setRechargeDialogOpen(true);
    };

    const handleRecharge = (packageId: number) => {
        if (!templeId) return;
        dispatch(initiateRechargeThunk({ packageId, templeId }))
            .then(() => {
                enqueueSnackbar('Recharge initiated successfully', { variant: 'success' });
                setRechargeDialogOpen(false);
                dispatch(fetchWalletThunk({ templeId }));
                dispatch(fetchRechargesThunk({ templeId }));
            })
            .catch(() => {
                enqueueSnackbar('Recharge failed. Please try again.', { variant: 'error' });
            });
    };

    return (
        <Box>
            {/* ─── Admin Tabs (global, no temple needed) ─── */}
            <Typography variant="h5" gutterBottom>WhatsApp Management</Typography>

            <Tabs
                value={tab < 3 ? false : tab}
                onChange={(_, newValue) => setTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab label="Admin Packages" value={3} />
                <Tab label="Admin Templates" value={4} />
            </Tabs>

            <TabPanel value={tab} index={3}>
                <AdminPackageTable />
            </TabPanel>

            <TabPanel value={tab} index={4}>
                <AdminTemplateTable />
            </TabPanel>

            {/* ─── Temple-Scoped Section ─── */}
            <Box mt={4} mb={2}>
                <Divider />
            </Box>
            <Typography variant="h6" gutterBottom>Temple WhatsApp</Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <TempleSearch handleTempleOpen={handleTempleOpen} />
                </Grid>

                {selectedTemple && (
                    <Grid item xs={12} md={8}>
                        <Box mb={1}>
                            <Typography variant="h6">{selectedTemple.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                ID: {selectedTemple.id}
                                {selectedTemple.isActive ? ' • Active' : ' • Inactive'}
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            {templeId ? (
                <Box mt={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <WalletCard wallet={wallet} api={walletApi} />
                        </Grid>
                        <Grid item xs={12}>
                            <Tabs
                                value={tab < 3 ? tab : false}
                                onChange={(_, newValue) => setTab(newValue)}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab label="Transactions" value={0} />
                                <Tab label="Recharges" value={1} />
                                <Tab label="Templates" value={2} />
                            </Tabs>

                            <TabPanel value={tab} index={0}>
                                <TransactionTable transactions={transactions} api={transactionsApi} />
                            </TabPanel>

                            <TabPanel value={tab} index={1}>
                                <Box display="flex" justifyContent="flex-end" mb={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleRechargeOpen}
                                    >
                                        Initiate Recharge
                                    </Button>
                                </Box>
                                <RechargeTable recharges={recharges} api={rechargesApi} />
                            </TabPanel>

                            <TabPanel value={tab} index={2}>
                                <TemplateTable templates={templates} api={templatesApi} />
                            </TabPanel>
                        </Grid>
                    </Grid>

                    <RechargeDialog
                        open={rechargeDialogOpen}
                        onClose={() => setRechargeDialogOpen(false)}
                        packages={packages}
                        packagesApi={packagesApi}
                        rechargeApi={rechargeInitiateApi}
                        onRecharge={handleRecharge}
                    />
                </Box>
            ) : (
                <Box textAlign="center" mt={4} mb={4}>
                    <Typography variant="body1" color="textSecondary">
                        Search and select a temple to view WhatsApp wallet, transactions, and templates
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default WhatsApp;
